import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Download, 
  ArrowLeft, 
  Users, 
  CheckCircle, 
  XCircle, 
  MusicNotes,
  Warning,
  Trash,
  Eye
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast, Toaster } from 'sonner'
import { rsvpService, isSupabaseConfigured, type DbRSVP } from '@/lib/supabase'
import guestListData from '../guestList.json'

const ADMIN_PASSWORD = 'stellateam'

type Guest = {
  name: string
  meal: 'salmon' | 'beef' | 'vegetarian' | 'vegan' | ''
  allergies: string
  isAttending: boolean
  isPlusOne?: boolean
}

type RSVPData = {
  id: string
  guests: Guest[]
  notAttending: string[]
  songRequest: string
  submittedAt: string
}

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState(false)
  
  const [dbRsvps, setDbRsvps] = useState<DbRSVP[]>([])
  const [loading, setLoading] = useState(true)
  const [supabaseConnected, setSupabaseConnected] = useState(false)

  useEffect(() => {
    const checkAuth = () => {
      if (sessionStorage.getItem('admin-auth') === 'true') {
        setAuthenticated(true)
      }
    }
    checkAuth()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      if (!authenticated) return
      
      setLoading(true)
      if (isSupabaseConfigured()) {
        setSupabaseConnected(true)
        const data = await rsvpService.getAll()
        setDbRsvps(data)
      } else {
        setSupabaseConnected(false)
      }
      setLoading(false)
    }
    fetchData()
  }, [authenticated])

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('admin-auth', 'true')
      setAuthenticated(true)
      setPasswordError(false)
    } else {
      setPasswordError(true)
    }
  }

  const handleLogout = () => {
    sessionStorage.removeItem('admin-auth')
    setAuthenticated(false)
  }

  // Normalize data from Supabase
  const rsvps: RSVPData[] = dbRsvps.map(r => ({
    id: r.group_id,
    guests: r.guests.map(g => ({
      name: g.name,
      meal: g.meal,
      allergies: (g as any).allergies || '',
      isAttending: g.is_attending,
      isPlusOne: g.is_plus_one
    })),
    notAttending: r.not_attending || [],
    songRequest: r.song_request,
    submittedAt: r.submitted_at
  }))

  // Calculate stats
  const totalAttending = rsvps.reduce((acc, r) => acc + r.guests.length, 0)
  const totalNotAttending = rsvps.reduce((acc, r) => acc + (r.notAttending?.length || 0), 0)
  const totalGroups = guestListData.groups.length
  const respondedGroups = rsvps.length
  const pendingGroups = totalGroups - respondedGroups

  // Meal counts
  const mealCounts = rsvps.reduce((acc, r) => {
    r.guests.forEach(g => {
      if (g.meal) {
        acc[g.meal] = (acc[g.meal] || 0) + 1
      }
    })
    return acc
  }, {} as Record<string, number>)

  // Song requests
  const songRequests = rsvps.filter(r => r.songRequest).map(r => ({
    group: r.id,
    song: r.songRequest
  }))

  // Groups that haven't responded
  const pendingGroupsList = guestListData.groups.filter(
    g => !rsvps.some(r => r.id === g.id)
  )

  const exportToCSV = () => {
    const headers = ['Group ID', 'Guest Name', 'Meal', 'Allergies/Dietary', 'Is Plus One', 'Song Request', 'Submitted At']
    const rows: string[][] = []

    rsvps.forEach(rsvp => {
      rsvp.guests.forEach((guest, index) => {
        rows.push([
          rsvp.id,
          guest.name,
          guest.meal,
          guest.allergies || '',
          guest.isPlusOne ? 'Yes' : 'No',
          index === 0 ? rsvp.songRequest : '',
          index === 0 ? new Date(rsvp.submittedAt).toLocaleString() : ''
        ])
      })
      // Add not attending guests
      rsvp.notAttending?.forEach(name => {
        rows.push([
          rsvp.id,
          name,
          'NOT ATTENDING',
          '',
          'No',
          '',
          ''
        ])
      })
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `wedding-rsvps-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    toast.success('CSV exported successfully!')
  }

  const exportMealCountsCSV = () => {
    const headers = ['Meal Type', 'Count']
    const rows = Object.entries(mealCounts).map(([meal, count]) => [meal, count.toString()])
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `meal-counts-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    toast.success('Meal counts exported!')
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl text-white">Admin Access</CardTitle>
              <CardDescription className="text-slate-400">
                Enter the admin password to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`bg-slate-700 border-slate-600 text-white ${passwordError ? 'border-red-500' : ''}`}
                />
                {passwordError && (
                  <p className="text-red-400 text-sm">Incorrect password</p>
                )}
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Login
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
        <Toaster position="top-center" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <a href="/" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </a>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">RSVP Admin</h1>
              <p className="text-slate-400 text-sm">
                {supabaseConnected ? '🟢 Connected to Supabase' : '🔴 Supabase not connected'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportToCSV} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={handleLogout} variant="ghost" className="text-slate-400 hover:text-white">
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{totalAttending}</p>
                  <p className="text-slate-400 text-sm">Attending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{totalNotAttending}</p>
                  <p className="text-slate-400 text-sm">Not Attending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{respondedGroups}/{totalGroups}</p>
                  <p className="text-slate-400 text-sm">Groups Responded</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Warning className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{pendingGroups}</p>
                  <p className="text-slate-400 text-sm">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Meal Counts */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Meal Counts</CardTitle>
            <Button onClick={exportMealCountsCSV} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { meal: 'salmon', label: 'Salmon', color: 'bg-rose-500' },
                { meal: 'beef', label: 'Beef', color: 'bg-orange-500' },
                { meal: 'vegetarian', label: 'Vegetarian', color: 'bg-green-500' },
                { meal: 'vegan', label: 'Vegan', color: 'bg-emerald-500' },
              ].map(({ meal, label, color }) => (
                <div key={meal} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                  <div className={`w-3 h-3 rounded-full ${color}`} />
                  <span className="text-slate-300">{label}</span>
                  <span className="ml-auto text-white font-bold">{mealCounts[meal] || 0}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* RSVPs Table */}
        <Card className="bg-slate-800/50 border-slate-700 mb-8">
          <CardHeader>
            <CardTitle className="text-white">All RSVPs</CardTitle>
            <CardDescription className="text-slate-400">
              {rsvps.length} responses received
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-slate-400">Loading...</div>
            ) : rsvps.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No RSVPs yet</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Group</TableHead>
                      <TableHead className="text-slate-400">Guests</TableHead>
                      <TableHead className="text-slate-400">Not Attending</TableHead>
                      <TableHead className="text-slate-400">Song</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rsvps.map((rsvp) => (
                      <TableRow key={rsvp.id} className="border-slate-700">
                        <TableCell className="text-white font-medium">{rsvp.id}</TableCell>
                        <TableCell className="text-slate-300">
                          <div className="space-y-1">
                            {rsvp.guests.map((g, i) => (
                              <div key={i} className="text-sm">
                                {g.name} <span className="text-slate-500">({g.meal})</span>
                                {g.isPlusOne && <span className="text-blue-400 ml-1">+1</span>}
                                {g.allergies && <span className="text-yellow-400 ml-1">⚠</span>}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {rsvp.notAttending?.length > 0 ? (
                            <div className="space-y-1">
                              {rsvp.notAttending.map((n, i) => (
                                <div key={i} className="text-sm">{n}</div>
                              ))}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-slate-300 max-w-[150px] truncate">
                          {rsvp.songRequest || '-'}
                        </TableCell>
                        <TableCell className="text-slate-400 text-sm">
                          {new Date(rsvp.submittedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-800 border-slate-700 text-white">
                              <DialogHeader>
                                <DialogTitle>{rsvp.id}</DialogTitle>
                                <DialogDescription className="text-slate-400">
                                  Submitted {new Date(rsvp.submittedAt).toLocaleString()}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <h4 className="text-sm font-medium text-slate-400 mb-2">Attending</h4>
                                  {rsvp.guests.map((g, i) => (
                                    <div key={i} className="py-1">
                                      <div className="flex justify-between">
                                        <span>{g.name} {g.isPlusOne && <span className="text-blue-400">(+1)</span>}</span>
                                        <span className="text-slate-400">{g.meal}</span>
                                      </div>
                                      {g.allergies && (
                                        <p className="text-sm text-yellow-400 ml-2">⚠ {g.allergies}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {rsvp.notAttending?.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-medium text-slate-400 mb-2">Not Attending</h4>
                                    {rsvp.notAttending.map((n, i) => (
                                      <div key={i} className="py-1 text-slate-500">{n}</div>
                                    ))}
                                  </div>
                                )}
                                {rsvp.songRequest && (
                                  <div>
                                    <h4 className="text-sm font-medium text-slate-400 mb-1">Song Request</h4>
                                    <p>{rsvp.songRequest}</p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Groups */}
        {pendingGroupsList.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Warning className="w-5 h-5 text-yellow-400" />
                Pending Responses
              </CardTitle>
              <CardDescription className="text-slate-400">
                Groups that haven't responded yet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingGroupsList.map((group) => (
                  <div key={group.id} className="p-3 bg-slate-700/50 rounded-lg">
                    <p className="text-white font-medium">{group.names.join(', ')}</p>
                    <p className="text-slate-500 text-sm">Party of {group.maxGuests}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Song Requests */}
        {songRequests.length > 0 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MusicNotes className="w-5 h-5 text-purple-400" />
                Song Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {songRequests.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-slate-700/50 rounded">
                    <MusicNotes className="w-4 h-4 text-purple-400" />
                    <span className="text-white">{s.song}</span>
                    <span className="text-slate-500 text-sm ml-auto">— {s.group}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Toaster position="top-center" />
    </div>
  )
}
